import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Users, Store, Truck, AlertCircle, ShoppingBag, TrendingUp, IndianRupee, Wallet, Clock, ChefHat, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import PendingSettlementsCard from '@/components/PendingSettlementsCard';
import PendingWithdrawalsCard from '@/components/PendingWithdrawalsCard';
import RecentTransactionsCard from '@/components/RecentTransactionsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  PENDING: '#F59E0B',
  ACCEPTED: '#3B82F6',
  PREPARING: '#8B5CF6',
  OUT_FOR_DELIVERY: '#06B6D4',
  DELIVERED: '#10B981',
  COMPLETED: '#10B981',
  CANCELLED: '#EF4444'
};

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n || 0));

const Dashboard = () => {
  const { admin, can } = useAuth();
  // Roles limited to today's stats (e.g. Order Manager) get a dedicated view.
  const todayOnly = !admin?.isSuperAdmin && admin?.dashboardScope === 'today';
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        if (todayOnly) {
          const [s, b] = await Promise.all([
            axios.get(`${API}/dashboard/stats`),
            axios.get(`${API}/dashboard/orders-breakdown`)
          ]);
          setStats(s.data);
          setBreakdown(b.data || []);
          return;
        }
        const [s, m, b] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/dashboard/sales-metrics?days=${days}`),
          axios.get(`${API}/dashboard/orders-breakdown`)
        ]);
        setStats(s.data);
        setSeries(m.data.series || []);
        setBreakdown(b.data || []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days, todayOnly, reloadKey]);

  const statCards = [
    { title: 'Total Restaurants', value: stats?.totalRestaurants ?? 0, icon: Store,         color: '#C2410C', bgColor: '#FFF7ED', testid: 'total-restaurants', path: '/restaurants' },
    { title: 'Total Users',       value: stats?.totalUsers ?? 0,       icon: Users,         color: '#3B82F6', bgColor: '#EFF6FF', testid: 'total-users', path: '/users' },
    { title: 'Delivery Agents',   value: stats?.totalDeliveryAgents ?? 0, icon: Truck,      color: '#10B981', bgColor: '#ECFDF5', testid: 'total-agents', path: '/delivery-agents' },
    { title: 'Pending Verifications', value: stats?.pendingVerifications ?? 0, icon: AlertCircle, color: '#F59E0B', bgColor: '#FFFBEB', testid: 'pending-verifications', path: '/restaurants?verification=pending' },
    { title: 'Total Orders',      value: stats?.totalOrders ?? 0,      icon: ShoppingBag,   color: '#7C3AED', bgColor: '#F5F3FF', testid: 'total-orders', path: '/orders' },
    { title: 'Total Revenue',     value: `₹${formatINR(stats?.totalRevenue)}`, icon: IndianRupee, color: '#0EA5E9', bgColor: '#F0F9FF', testid: 'total-revenue', path: '/transactions' },
    { title: 'Total Profit',      value: `₹${formatINR(stats?.totalProfit)}`,  icon: TrendingUp,  color: '#059669', bgColor: '#ECFDF5', testid: 'total-profit', path: '/transactions' },
    // Live sum across all customer wallets — a top-up increments the
    // customer's walletBalance immediately (see wallet_update in
    // foodk_api), and this figure is fetched fresh on every dashboard
    // load, so it always reflects the current total, no separate refresh
    // step needed.
    { title: 'Customer Wallet Balance', value: `₹${formatINR(stats?.totalUserWalletBalance)}`, icon: Wallet, color: '#DB2777', bgColor: '#FDF2F8', testid: 'total-wallet-balance', path: '/users' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalBreakdown = breakdown.reduce((a, b) => a + b.count, 0) || 1;

  if (todayOnly) {
    const todayCards = [
      { title: "Today's Orders",    value: stats?.todayOrders ?? 0,         icon: ShoppingBag, color: '#7C3AED', bgColor: '#F5F3FF', testid: 'today-orders' },
      { title: "Today's Revenue",   value: `₹${formatINR(stats?.todayRevenue)}`, icon: IndianRupee, color: '#0EA5E9', bgColor: '#F0F9FF', testid: 'today-revenue' },
      { title: 'Pending',           value: stats?.todayPending ?? 0,        icon: Clock,       color: '#F59E0B', bgColor: '#FFFBEB', testid: 'today-pending' },
      { title: 'In Kitchen',        value: stats?.todayInKitchen ?? 0,      icon: ChefHat,     color: '#8B5CF6', bgColor: '#F5F3FF', testid: 'today-kitchen' },
      { title: 'Out for Delivery',  value: stats?.todayOutForDelivery ?? 0, icon: Truck,       color: '#06B6D4', bgColor: '#ECFEFF', testid: 'today-out-for-delivery' },
      { title: 'Completed',         value: stats?.todayCompleted ?? 0,      icon: CheckCircle2, color: '#10B981', bgColor: '#ECFDF5', testid: 'today-completed' },
      { title: 'Cancelled',         value: stats?.todayCancelled ?? 0,      icon: XCircle,     color: '#EF4444', bgColor: '#FEF2F2', testid: 'today-cancelled' },
    ];
    return (
      <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }} data-testid="dashboard-today">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
              Welcome back, {admin?.name}!
            </h1>
            <p className="text-gray-600">Today&apos;s order activity.</p>
          </div>
          <button
            type="button"
            onClick={() => { setLoading(true); setReloadKey((k) => k + 1); }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white hover:bg-gray-50"
            data-testid="dashboard-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="dashboard-stats">
          {todayCards.map((card) => {
            const Icon = card.icon;
            const clickable = can('orders', 'view');
            return (
              <div
                key={card.title}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => navigate('/orders') : undefined}
                className={`bg-white rounded-xl border border-gray-200 p-6 transition-all ${clickable ? 'hover:shadow-md hover:border-orange-300 cursor-pointer' : ''}`}
                data-testid={`stat-card-${card.testid}`}
              >
                <div className="p-3 rounded-lg inline-block mb-4" style={{ backgroundColor: card.bgColor }}>
                  <Icon size={24} style={{ color: card.color }} />
                </div>
                <h3 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
                  {card.value}
                </h3>
                <p className="text-sm text-gray-600">{card.title}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
          Welcome back, {admin?.name}!
        </h1>
        <p className="text-gray-600">Here's what's happening with your platform today.</p>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="dashboard-stats">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              role="button"
              tabIndex={0}
              onClick={() => navigate(card.path)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(card.path); } }}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md hover:border-orange-300 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-300"
              data-testid={`stat-card-${card.testid}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: card.bgColor }}>
                  <Icon size={24} style={{ color: card.color }} />
                </div>
              </div>
              <h3 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
                {card.value}
              </h3>
              <p className="text-sm text-gray-600">{card.title}</p>
            </div>
          );
        })}
      </div>

      {/* SALES METRICS */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 lg:col-span-2" data-testid="sales-metrics-chart">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sales Metrics
              </h3>
              <p className="text-sm text-gray-500">Revenue and orders over the last {days} days</p>
            </div>
            <div className="flex gap-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  data-testid={`sales-range-${d}`}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    days === d
                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {d}D
                </button>
              ))}
            </div>
          </div>

          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C2410C" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C2410C" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }}
                  formatter={(value, name) =>
                    name === 'orders' ? [value, 'Orders'] : [`₹${formatINR(value)}`, name === 'revenue' ? 'Revenue' : 'Profit']
                  }
                />
                <Area type="monotone" dataKey="revenue" stroke="#C2410C" strokeWidth={2} fill="url(#revGrad)" />
                <Area type="monotone" dataKey="profit"  stroke="#059669" strokeWidth={2} fill="url(#profitGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ORDER STATUS BREAKDOWN */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="order-breakdown">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Order Status
          </h3>
          {breakdown.length === 0 ? (
            <p className="text-sm text-gray-500">No orders yet.</p>
          ) : (
            <>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={breakdown}
                      dataKey="count"
                      nameKey="status"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {breakdown.map((b) => (
                        <Cell key={b.status} fill={STATUS_COLORS[b.status] || '#94A3B8'} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1">
                {breakdown.map((b) => (
                  <div key={b.status} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: STATUS_COLORS[b.status] || '#94A3B8' }}
                      />
                      <span className="text-gray-700">{b.status}</span>
                    </span>
                    <span className="text-gray-500">
                      {b.count} · {Math.round((b.count / totalBreakdown) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* PENDING SETTLEMENTS + WITHDRAW REQUESTS */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PendingSettlementsCard />
        <PendingWithdrawalsCard />
      </div>

      {/* RECENT TRANSACTIONS */}
      <div className="mt-8">
        {can('transactions', 'view') && <RecentTransactionsCard />}
      </div>

      {/* QUICK ACTIONS + STATUS */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quick Actions
          </h3>
          <div className="space-y-3">
            <a href="/restaurants" className="block p-4 rounded-lg hover:bg-orange-50 transition-colors border border-gray-200">
              <p className="font-medium" style={{ color: '#C2410C' }}>Manage Restaurants</p>
              <p className="text-sm text-gray-600">View and approve restaurant applications</p>
            </a>
            <a href="/delivery-agents" className="block p-4 rounded-lg hover:bg-orange-50 transition-colors border border-gray-200">
              <p className="font-medium" style={{ color: '#C2410C' }}>Manage Delivery Agents</p>
              <p className="text-sm text-gray-600">Verify and track agent performance</p>
            </a>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            System Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
              <span className="text-sm font-medium">Database</span>
              <span className="text-xs font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">Active</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
              <span className="text-sm font-medium">API Services</span>
              <span className="text-xs font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
